import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: var(--background-color);
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid var(--border-color);
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  margin-top: 16px;
  color: var(--text-secondary);
  font-size: 16px;
`;

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissionLevel?: number;
  requiredPermissionLevels?: number[];
  // בלעדי לתפקידים מסוימים - SystemAdmin (level 1) לא מקבל bypass
  exactPermissionLevels?: number[];
  requireSupervisor?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissionLevel,
  requiredPermissionLevels,
  exactPermissionLevels,
  requireSupervisor = false
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <LoadingContainer>
        <div>
          <LoadingSpinner />
          <LoadingText>טוען...</LoadingText>
        </div>
      </LoadingContainer>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permission level and supervisor requirements if specified
  if (user && (requiredPermissionLevel !== undefined || requiredPermissionLevels !== undefined || exactPermissionLevels !== undefined || requireSupervisor)) {
    // בלעדיות מוחלטת - ללא bypass של SystemAdmin (משמש את עמוד ניהול הפרמטרים)
    if (exactPermissionLevels !== undefined) {
      if (!exactPermissionLevels.includes(user.permissionLevel)) {
        return <Navigate to="/unauthorized" replace />;
      }
    } else if (requiredPermissionLevel !== undefined) {
      // Legacy single permission level check (system admin always has access)
      if (user.permissionLevel > requiredPermissionLevel && user.permissionLevel !== 1) {
        return <Navigate to="/unauthorized" replace />;
      }
    } else if (requiredPermissionLevels !== undefined) {
      // New multiple permission levels check (system admin always has access)
      if (!requiredPermissionLevels.includes(user.permissionLevel) && user.permissionLevel !== 1) {
        return <Navigate to="/unauthorized" replace />;
      }
    }
    
    // Check if supervisor privilege is required
    if (requireSupervisor && !user.isSupervisor && user.permissionLevel !== 1) {
      // Only system admins (level 1) or supervisors can access supervisor-only routes
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;